import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  ALL_EVENT,
  EMPTY_STRING,
  CURRENCY_SYMBOL,
  customerConstants,
  eventBookingStatus,
  CART_SUMMARY_STATUS,
  SHUTTERB_DETAILS_KEYS,
  eventBookingConstants,
  CART_TABLE_HEADER_KEYS,
  DEFAULT_VALUE_CART_TABLE,
} from "../../../../constants";
import recapImg from "../../../../assets/images/recap.png";
import { ReactComponent as DeleteIcon } from "../../../../assets/icons/delete.svg";
import { ReactComponent as ChevronDownSvg } from "../../../../assets/icons/chevron-down.svg";

import {
  numberToDecimals,
  removeDraftEvents,
  mergeRecapzWithBackup,
  attachRecapToFormState,
  generateAllEventsRecap,
  generateShutterbDetails,
  removeShutterBFromEvent,
  syncNotifyAdminOrderItems,
  calculateAllRecapRateTotalFee,
  calculateShutterAndRecapTotalFee,
  markShutterBUnselectedInDraftEvent,
  filterSelectedShutterBsInEditDraftEvents,
  markDurationIncreaseFlag,
} from "../../../../shared";
import { getItemFromStorage } from "../../../../system";
import CartSummaryMobileView from "./CartSummaryMobileView";
import { eventBookingActions } from "../../../../redux/actions";
import useResponsiveView from "../../../../shared/hooks/useResponsiveView";
import IconButton from "../../../../shared/components/IconButton/IconButton";
import ProfileImage from "../../../../shared/components/ProfileImage/ProfileImage";

const { TABS_ID } = eventBookingConstants;

const getCartSummaryColumns = ({
  notNewEvent,
  isTip,
  isExtend,
  isTipAndExtend,
  isEditEvent,
}) => {
  const columns = [
    {
      key: CART_TABLE_HEADER_KEYS.EVENT_NAME,
      label: "event name",
      minWidth: "80px",
      textAlign: "left",
      verticalAlign: "top",
    },
    {
      key: CART_TABLE_HEADER_KEYS.DETAILS,
      label: "details",
      minWidth: "100px",
      textAlign: "left",
      verticalAlign: "top",
    },
    ...(isEditEvent
      ? [
          {
            key: CART_TABLE_HEADER_KEYS.STATUS,
            label: "status",
            minWidth: "70px",
            textAlign: "center",
            verticalAlign: "middle",
          },
        ]
      : []),
    ...(isExtend || isTipAndExtend || !notNewEvent
      ? [
          {
            key: CART_TABLE_HEADER_KEYS.QUANTITY,
            label: "qty/hrs",
            minWidth: "80px",
            textAlign: "center",
            verticalAlign: "middle",
          },
          {
            key: CART_TABLE_HEADER_KEYS.RATE,
            label: "rate",
            minWidth: "80px",
            textAlign: "center",
            verticalAlign: "middle",
          },
        ]
      : []),
    ...(isTip || isTipAndExtend
      ? [
          {
            key: CART_TABLE_HEADER_KEYS.TIP,
            label: "tip",
            minWidth: "80px",
            textAlign: "center",
            verticalAlign: "middle",
          },
        ]
      : []),
    {
      key: CART_TABLE_HEADER_KEYS.TOTAL,
      label: "total",
      minWidth: "80px",
      textAlign: "center",
      verticalAlign: "middle",
    },
    {
      key: CART_TABLE_HEADER_KEYS.DELETE,
      label: "",
      minWidth: "30px",
      textAlign: "right",
      verticalAlign: "middle",
    },
  ];

  return columns;
};

const CartSummaryTable = (props) => {
  const {
    updatedFormState,
    setUpdatedFormState,
    initialTotalFeeRef,
    isSBTipExtendCart,
    isEditEvent,
    totalFeeBackupRef,
    setAreadyPaidAmount,
    editEventOrderDetails,
  } = props;

  const { t } = useTranslation("common");
  const { isLessThan } = useResponsiveView();

  // State to manage which accordions are open
  const [openAccordions, setOpenAccordions] = useState([]);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (props.activeTab === TABS_ID.CART && updatedFormState?.length > 0) {
      setOpenAccordions(updatedFormState.map(() => true));
      hasInitializedRef.current = true;
    }
  }, [updatedFormState, props.activeTab]);

  const cartPreviousScreen = getItemFromStorage(
    customerConstants.CART_PREVIOUS_SCREEN
  );
  const notNewEvent = getItemFromStorage(customerConstants.NOT_NEW_EVENT);

  const { isTip, isExtend, isTipAndExtend, cartSummaryColumns } =
    useMemo(() => {
      const isTipAndExtend =
        cartPreviousScreen === customerConstants.EXTEND_AND_TIP_TO_CART;

      const isTip =
        cartPreviousScreen === customerConstants.TIP_TO_CART || isTipAndExtend;

      const isExtend =
        cartPreviousScreen === customerConstants.EXTEND_TO_CART ||
        isTipAndExtend;

      const cartSummaryColumns = getCartSummaryColumns({
        notNewEvent,
        isTip,
        isExtend,
        isTipAndExtend,
        isEditEvent,
      });

      return {
        isTip,
        isExtend,
        isTipAndExtend,
        cartSummaryColumns,
      };
      // eslint-disable-next-line
    }, [cartPreviousScreen, notNewEvent, isEditEvent]);

  useEffect(() => {
    if (isTip || isExtend || isTipAndExtend) {
      props.setIsSBTipExtendCart(true);
    } else {
      props.setIsSBTipExtendCart(false);
    }
    // eslint-disable-next-line
  }, [isTip, isExtend, isTipAndExtend]);

  const allEventsRecaps = useMemo(() => {
    if (props.activeTab !== TABS_ID.CART) return [];
    return generateAllEventsRecap(
      props.addRecapz,
      props.recapTypes,
      props.addRecapzBackup
    );
  }, [
    props.activeTab,
    props.addRecapz,
    props.recapTypes,
    props.addRecapzBackup,
  ]);

  useEffect(() => {
    if (props.activeTab === TABS_ID.CART) {
      if (props.formState?.length > 0) {
        let filteredFormState;
        props.isDraftEvent
          ? (filteredFormState = filterSelectedShutterBsInEditDraftEvents(
              props.formState
            ))
          : (filteredFormState = removeDraftEvents(props.formState));

        if (props.recapTypes?.length > 0) {
          // Merge local recaps with those from the backend.
          // If a match is found, reuse the backend recap IDs and update the status to "edited" or "cancelled".
          const mergedRecapz = mergeRecapzWithBackup(
            props.addRecapz,
            props.addRecapzBackup
          );

          // set orderitems status to notify_admin if the event's is notify_admin
          const syncedFormState = syncNotifyAdminOrderItems(
            filteredFormState,
            props.editEvents?.events
          );

          // Set flag isDurationIncrease with true/false in events orderitems
          // If quantity is increase then set to true
          // and if quantity is remain equal or decrease then set to false
          const updateFormStateWithDurationFlag =
            markDurationIncreaseFlag(syncedFormState);

          // Attach recaps to the formState event so they display under their corresponding event.
          const resultWithRecaps = attachRecapToFormState(
            updateFormStateWithDurationFlag,
            mergedRecapz,
            props.recapTypes
          );

          setUpdatedFormState(resultWithRecaps);
        }
      } else if (isSBTipExtendCart) {
        setUpdatedFormState([]);
      }
    }
    // eslint-disable-next-line
  }, [
    props.formState,
    props.addRecapz,
    props.activeTab,
    props.recapTypes,
    props.addRecapzBackup,
    props.editEvents,
  ]);

  useEffect(() => {
    if (props.activeTab === TABS_ID.CART) {
      const { tipTotal, shutterAndRecapTotal } =
        calculateShutterAndRecapTotalFee(updatedFormState);
      const allRecapRateTotal = calculateAllRecapRateTotalFee(allEventsRecaps);

      let totalFee = shutterAndRecapTotal + allRecapRateTotal;
      let remainingTotalFee =
        totalFee - editEventOrderDetails?.totalFeeWithDiscount;

      if (props.isEditEvent) {
        if (remainingTotalFee < 0) {
          // Apply the previous discount on refunded items
          const previousDiscountApplied =
            Math.abs(remainingTotalFee) *
            (editEventOrderDetails?.discountPercentage || 0);

          remainingTotalFee -=
            previousDiscountApplied * Math.sign(remainingTotalFee);
          totalFee -= previousDiscountApplied;
        } else if (remainingTotalFee > 0) {
          // Case 2: apply active non-gift-card discounts
          const nonGiftCardDiscounts =
            editEventOrderDetails?.orderDiscountDetails?.filter(
              (d) => !d.isGiftCard && d.type === CURRENCY_SYMBOL.PERCENT
            ) || [];

          nonGiftCardDiscounts.forEach((discount) => {
            const discountAmount = remainingTotalFee * (discount.amount / 100);
            remainingTotalFee -= discountAmount;
            totalFee -= discountAmount;
          });
        }
      }

      // use totalFeeBackupRef to reset the fee value on checkout
      // if user change the discount (promo code) or want to apply new discount
      totalFeeBackupRef.current = totalFee;
      initialTotalFeeRef.current = remainingTotalFee;
      props.setTotalFee(remainingTotalFee);
      setAreadyPaidAmount(editEventOrderDetails?.totalFeeWithDiscount);
      props.setTotalTip(tipTotal);
    }

    // eslint-disable-next-line
  }, [updatedFormState, allEventsRecaps]);

  const toggleAccordion = (index) => {
    setOpenAccordions((prev) =>
      prev.map((val, i) => (i === index ? !val : val))
    );
  };

  const handleDeleteShutterB = (
    eventId,
    shutterBId,
    eventIndex,
    shutterBIndex
  ) => {
    if (isSBTipExtendCart) {
      const updatedFormState = [...props.formState];
      const event = { ...updatedFormState[eventIndex] }; // current event
      const shutterBs = [...event.orderItems]; // shutterBs

      // Remove the shutterB at the specific index
      shutterBs.splice(shutterBIndex, 1);

      if (shutterBs.length === 0) {
        // No shutterBs left, remove the whole event
        updatedFormState.splice(eventIndex, 1);
      } else {
        // Update the event's orderItems
        event.orderItems = shutterBs;
        updatedFormState[eventIndex] = event;
      }
      props.updateCustomerDashboardCart(updatedFormState);
      props.setFormState(updatedFormState);
    } else if (props.isDraftEvent) {
      const updatedFormState = markShutterBUnselectedInDraftEvent(
        props.formState,
        eventId,
        shutterBId
      );
      props.setFormState(updatedFormState);
    } else {
      const updatedFormState = removeShutterBFromEvent(
        props.formState,
        eventId,
        shutterBId
      );
      props.setFormState(updatedFormState);
    }
  };

  const handleDeleteRecap = (eventId, recapType) => {
    const updatedRecapz = props.addRecapz
      .map((item) => {
        if (item.eventId !== eventId) return item;
        const updatedRecap = item.reelDetail.filter(
          (r) => r.reelMode !== recapType
        );
        const updatedQuantity = updatedRecap.length;

        // If nothing left, we return null to filter it out later
        if (updatedQuantity === 0) return null;

        return {
          ...item,
          reelDetail: updatedRecap,
          quantity: updatedQuantity,
        };
      })
      .filter(Boolean); // remove null entries

    props.setAddRecapz(updatedRecapz);
  };

  const renderRecapTableRow = (col, reelItem, event) => {
    switch (col.key) {
      case CART_TABLE_HEADER_KEYS.EVENT_NAME:
        return (
          <span className="shutterb-profile-image">
            <img src={recapImg} alt="reelItem" />
          </span>
        );

      case CART_TABLE_HEADER_KEYS.DETAILS:
        return (
          <div className="label-content recap-deleted">
            {`${
              event.name ? `${event.name} – ` : EMPTY_STRING
            }${reelItem.reelMode.toUpperCase()}`}
          </div>
        );

      case CART_TABLE_HEADER_KEYS.STATUS:
        return (
          <div className="text-center recap-deleted">
            {reelItem?.status ? (
              <span
                className={
                  CART_SUMMARY_STATUS[reelItem.status]?.className ||
                  EMPTY_STRING
                }
              >
                {CART_SUMMARY_STATUS[reelItem.status]?.label || reelItem.status}
              </span>
            ) : (
              DEFAULT_VALUE_CART_TABLE
            )}
          </div>
        );

      case CART_TABLE_HEADER_KEYS.QUANTITY:
        return (
          <div className="text-center recap-deleted">{reelItem.quantity}</div>
        );

      case CART_TABLE_HEADER_KEYS.RATE:
        return (
          <div className="text-center recap-deleted">${reelItem.price}</div>
        );

      case CART_TABLE_HEADER_KEYS.TOTAL:
        return (
          <div className="recap-deleted">
            ${numberToDecimals(reelItem.amount)}
          </div>
        );

      case CART_TABLE_HEADER_KEYS.DELETE:
        return (
          <div className="d-flex justify-content-center">
            <IconButton
              icon={<DeleteIcon />}
              disabled={reelItem?.status === eventBookingStatus.CANCEL}
              handleClick={() => handleDeleteRecap(event.id, reelItem.reelMode)}
            />
          </div>
        );

      default:
        return EMPTY_STRING;
    }
  };

  return (
    <div className="py-4 cart-summary-table">
      <h2 className="cart-summary-title mb-3">{t("review_your_order")}</h2>

      {isLessThan(850) ? (
        <CartSummaryMobileView
          updatedFormState={updatedFormState}
          allEventsRecaps={allEventsRecaps}
          handleDeleteShutterB={handleDeleteShutterB}
          handleDeleteRecap={handleDeleteRecap}
          isSBTipExtendCart={isSBTipExtendCart}
          recapTypes={props.recapTypes}
          isEditEvent={isEditEvent}
        />
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered table-hover">
            <thead className="thead">
              <tr>
                {cartSummaryColumns.map((col) => (
                  <th
                    key={col.key}
                    style={{
                      minWidth: col.minWidth,
                      textAlign: col.textAlign,
                      verticalAlign: "middle",
                    }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {updatedFormState?.map((event, index) => (
                <React.Fragment key={event.id}>
                  <tr
                    className="table-primary cursor-pointer"
                    onClick={() => toggleAccordion(index)}
                  >
                    <td
                      colSpan={cartSummaryColumns.length}
                      style={{
                        verticalAlign: "middle",
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center event-title">
                        <span className="accordion-title">
                          {event.eventName}
                        </span>
                        <span className="accordion-icon mb-3">
                          <ChevronDownSvg
                            className={`${
                              openAccordions[index]
                                ? "rotate-180"
                                : EMPTY_STRING
                            }`}
                          />
                        </span>
                      </div>
                    </td>
                  </tr>

                  {openAccordions[index] && (
                    <>
                      {event?.orderItems?.map((shutterB, _index) => (
                        <tr
                          key={`${shutterB.sbUserId}-${event.id}-${_index}`}
                          className={`${
                            shutterB?.status === eventBookingStatus.CANCEL
                              ? "cart-summary-table-deleted"
                              : EMPTY_STRING
                          }`}
                        >
                          {cartSummaryColumns.map((col) => (
                            <td
                              key={col.key}
                              style={{
                                minWidth: col.minWidth,
                                verticalAlign: col.verticalAlign,
                                textAlign: col.textAlign,
                              }}
                              className="text-content"
                            >
                              {col.key === CART_TABLE_HEADER_KEYS.EVENT_NAME ? (
                                <span className="shutterb-profile-image">
                                  <ProfileImage
                                    imageUrl={
                                      shutterB?.sbportfolioGalleries?.[0]
                                        ?.profileImageUrl ||
                                      shutterB?.sbUser?.sbPortfolios?.[0]
                                        ?.profileImageUrl ||
                                      EMPTY_STRING
                                    }
                                  />
                                </span>
                              ) : col.key === CART_TABLE_HEADER_KEYS.DETAILS ? (
                                <div>
                                  {generateShutterbDetails(shutterB, event, [
                                    SHUTTERB_DETAILS_KEYS.SHUTTERB,
                                    SHUTTERB_DETAILS_KEYS.DATE,
                                    SHUTTERB_DETAILS_KEYS.START_TIME,
                                    SHUTTERB_DETAILS_KEYS.DURATION,
                                  ]).map((item, idx) => (
                                    <div
                                      className="d-flex align-items-center"
                                      key={idx}
                                    >
                                      <p className="shutterb-title shutterb-deleted label-content-600 mb-0">
                                        {item.label}
                                      </p>
                                      <p className="shutterb-deleted text-content mb-0">
                                        {item.value}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              ) : col.key === CART_TABLE_HEADER_KEYS.STATUS ? (
                                <div className="text-center shutterb-deleted">
                                  {shutterB?.status ? (
                                    <span
                                      className={
                                        CART_SUMMARY_STATUS[shutterB.status]
                                          ?.className || EMPTY_STRING
                                      }
                                    >
                                      {CART_SUMMARY_STATUS[shutterB.status]
                                        ?.label || shutterB.status}
                                    </span>
                                  ) : (
                                    DEFAULT_VALUE_CART_TABLE
                                  )}
                                </div>
                              ) : col.key ===
                                CART_TABLE_HEADER_KEYS.QUANTITY ? (
                                <div className="text-center shutterb-deleted">
                                  {shutterB?.tipPrice_
                                    ? DEFAULT_VALUE_CART_TABLE
                                    : shutterB?.extendedQuantity_
                                    ? shutterB?.extendedQuantity_
                                    : event?.duration}
                                </div>
                              ) : col.key === CART_TABLE_HEADER_KEYS.RATE ? (
                                <div className="text-center shutterb-deleted">
                                  {shutterB?.tipPrice_
                                    ? DEFAULT_VALUE_CART_TABLE
                                    : shutterB?.isDurationIncrease
                                    ? `$${numberToDecimals(
                                        shutterB?.hourlyRate ||
                                          shutterB?.extendedPrice_ ||
                                          0
                                      )}`
                                    : `$${shutterB?.oldHourlyRate || 0}`}
                                </div>
                              ) : col.key === CART_TABLE_HEADER_KEYS.TIP ? (
                                <div className="text-center">
                                  {shutterB?.tipPrice_
                                    ? `$${shutterB?.tipPrice_}`
                                    : DEFAULT_VALUE_CART_TABLE}
                                </div>
                              ) : col.key === CART_TABLE_HEADER_KEYS.TOTAL ? (
                                <div className="shutterb-deleted">
                                  $
                                  {!shutterB?.tipPrice_
                                    ? numberToDecimals(
                                        shutterB?.extendedPrice_
                                          ? shutterB.extendedQuantity_ *
                                              shutterB?.extendedPrice_
                                          : shutterB?.isDurationIncrease
                                          ? event.duration *
                                            shutterB?.hourlyRate
                                          : event.duration *
                                            shutterB?.oldHourlyRate
                                      )
                                    : shutterB?.tipPrice_}
                                </div>
                              ) : col.key === CART_TABLE_HEADER_KEYS.DELETE ? (
                                <div className=" d-flex justify-content-center">
                                  <IconButton
                                    disabled={
                                      shutterB.status ===
                                        eventBookingStatus.CANCEL ||
                                      event.status ===
                                        eventBookingStatus.NOTIFY_ADMIN ||
                                      (event.orderItems?.filter(f => f.status !== eventBookingStatus.CANCEL)?.length === 1 &&
                                        !isSBTipExtendCart)
                                    }
                                    icon={<DeleteIcon />}
                                    handleClick={() =>
                                      handleDeleteShutterB(
                                        event.id,
                                        shutterB.sbUserId,
                                        index,
                                        _index
                                      )
                                    }
                                  />
                                </div>
                              ) : (
                                EMPTY_STRING
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}

                      {/* Show reelItem by event Id */}
                      {event?.reels?.map((reelItem, recapIndex) => (
                        <tr
                          key={`reelItem-${event.id}-${recapIndex}`}
                          className={`table-light ${
                            reelItem?.status === eventBookingStatus.CANCEL
                              ? "cart-summary-table-deleted"
                              : EMPTY_STRING
                          }`}
                        >
                          {cartSummaryColumns.map((col) => (
                            <td
                              key={col.key}
                              style={{
                                minWidth: col.minWidth,
                                verticalAlign:
                                  col.key === CART_TABLE_HEADER_KEYS.DETAILS
                                    ? "middle"
                                    : col.verticalAlign,
                                textAlign: col.textAlign,
                              }}
                              className="text-content"
                            >
                              {renderRecapTableRow(col, reelItem, event)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </>
                  )}
                </React.Fragment>
              ))}

              {allEventsRecaps?.map((reelItem, index) => (
                <tr
                  key={`all-events-recap-${index}`}
                  className={`${
                    index === 0 ? "all-events-recap" : EMPTY_STRING
                  } ${
                    reelItem?.status === eventBookingStatus.CANCEL
                      ? "cart-summary-table-deleted"
                      : EMPTY_STRING
                  }`}
                >
                  {cartSummaryColumns.map((col) => (
                    <td
                      key={col.key}
                      style={{
                        minWidth: col.minWidth,
                        verticalAlign:
                          col.key === CART_TABLE_HEADER_KEYS.DETAILS
                            ? "middle"
                            : col.verticalAlign,
                        textAlign: col.textAlign,
                      }}
                      className="text-content"
                    >
                      {renderRecapTableRow(col, reelItem, ALL_EVENT)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  formState: state.eventBooking.formState,
  addRecapz: state.eventBooking.addRecapz,
  recapTypes: state.eventBooking.recapTypes,
  editEvents: state.eventBooking.editEvents,
  isEditEvent: state.eventBooking.isEditEvent,
  isDraftEvent: state.eventBooking.isDraftEvent,
  addRecapzBackup: state.eventBooking.addRecapzBackup,
  isSBTipExtendCart: state.eventBooking.isSBTipExtendCart,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  setAddRecapz: (data) => dispatch(eventBookingActions.setAddRecapz(data)),
  setFormState: (data) => dispatch(eventBookingActions.setFormState(data)),
  setIsSBTipExtendCart: (data) =>
    dispatch(eventBookingActions.setIsSBTipExtendCart(data)),
  updateCustomerDashboardCart: (dataObj) =>
    dispatch({
      type: customerConstants.UPDATE_CUSTOMER_DASHBOARD_CART,
      data: dataObj,
    }),
});

// mapping action and store the function via props
export default connect(mapStateToProps, mapDispatchToProps)(CartSummaryTable);
